import { useEffect, useState } from "react";
import { useSession } from "@/auth/session";
import { getSettingsSnapshot, updateInvoiceIntegrationSettings } from "@/lib/api";

type DteForm = {
  pathDte: string;
  rutEmpresa: string;
  apiSecret: string;
  apiKey: string;
};

const defaultForm: DteForm = {
  pathDte: "",
  rutEmpresa: "",
  apiSecret: "",
  apiKey: "",
};

export default function DTETab() {
  const { session } = useSession();
  const [form, setForm] = useState<DteForm>(defaultForm);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    let cancelled = false;

    async function load(): Promise<void> {
      if (!session) return;
      setLoading(true);
      setError("");
      try {
        const snapshot = await getSettingsSnapshot(session);
        if (cancelled) return;

        const extraJson = (snapshot.invoiceIntegration?.extraJson ?? {}) as Record<string, unknown>;
        setForm({
          pathDte:
            (typeof snapshot.invoiceIntegration?.endpoint === "string" && snapshot.invoiceIntegration.endpoint.trim().length > 0)
              ? snapshot.invoiceIntegration.endpoint
              : (typeof extraJson.pathDte === "string" ? extraJson.pathDte : ""),
          rutEmpresa: typeof extraJson.rutEmpresa === "string" ? extraJson.rutEmpresa : "",
          apiSecret: typeof snapshot.invoiceIntegration?.apiSecretEncrypted === "string" ? snapshot.invoiceIntegration.apiSecretEncrypted : "",
          apiKey: typeof snapshot.invoiceIntegration?.apiKeyEncrypted === "string" ? snapshot.invoiceIntegration.apiKeyEncrypted : "",
        });
      } catch (e) {
        if (!cancelled) {
          setError(e instanceof Error ? e.message : "No se pudo cargar configuración DTE");
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    }

    void load();
    return () => {
      cancelled = true;
    };
  }, [session]);

  function setField<K extends keyof DteForm>(key: K, value: DteForm[K]): void {
    setForm((prev) => ({ ...prev, [key]: value }));
    setSaved(false);
  }

  async function handleSave(): Promise<void> {
    if (!session) return;
    setSaving(true);
    setError("");
    try {
      const current = await getSettingsSnapshot(session);
      const extraJson = (current.invoiceIntegration?.extraJson ?? {}) as Record<string, unknown>;

      await updateInvoiceIntegrationSettings(session, {
        providerName: current.invoiceIntegration?.providerName ?? "dte_cloud",
        endpoint: form.pathDte.trim(),
        apiKeyEncrypted: form.apiKey.trim(),
        apiSecretEncrypted: form.apiSecret.trim(),
        status: current.invoiceIntegration?.status ?? "active",
        extraJson: {
          ...extraJson,
          pathDte: form.pathDte,
          rutEmpresa: form.rutEmpresa,
        },
      });

      setSaved(true);
      setTimeout(() => setSaved(false), 2500);
    } catch (e) {
      setError(e instanceof Error ? e.message : "No se pudo guardar configuración DTE");
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="space-y-5">
      {error ? <div className="rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-700">{error}</div> : null}
      {loading ? <div className="text-sm text-gray-500">Cargando configuración DTE...</div> : null}

      <div className="bg-white rounded-xl border border-gray-100 p-6">
        <div className="flex items-center gap-3 mb-5">
          <div className="w-8 h-8 flex items-center justify-center rounded-lg bg-indigo-50">
            <i className="ri-file-shield-2-line text-indigo-600" />
          </div>
          <div>
            <h2 className="text-sm font-semibold text-gray-800">Configuración DTE</h2>
            <p className="text-xs text-gray-400">Parámetros de integración para documentos tributarios electrónicos</p>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Field label="Path DTE">
            <input
              value={form.pathDte}
              onChange={(e) => setField("pathDte", e.target.value)}
              placeholder="/ruta/dte"
              className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg"
            />
          </Field>

          <Field label="RUT empresa">
            <input
              value={form.rutEmpresa}
              onChange={(e) => setField("rutEmpresa", e.target.value)}
              placeholder="12.345.678-9"
              className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg"
            />
          </Field>

          <Field label="API secret">
            <input
              type="password"
              value={form.apiSecret}
              onChange={(e) => setField("apiSecret", e.target.value)}
              placeholder="Ingresa API secret"
              className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg"
            />
          </Field>

          <Field label="API key">
            <input
              value={form.apiKey}
              onChange={(e) => setField("apiKey", e.target.value)}
              placeholder="Ingresa API key"
              className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg"
            />
          </Field>
        </div>
      </div>

      <div className="flex items-center justify-end gap-3">
        {saved ? (
          <div className="flex items-center gap-2 text-emerald-600 text-sm">
            <div className="w-4 h-4 flex items-center justify-center"><i className="ri-check-line" /></div>
            Configuración guardada
          </div>
        ) : null}
        <button
          onClick={() => void handleSave()}
          disabled={saving || loading}
          className="px-5 py-2 bg-blue-600 text-white text-sm font-medium rounded-lg hover:bg-blue-700 disabled:opacity-60"
        >
          {saving ? "Guardando..." : "Guardar configuración"}
        </button>
      </div>
    </div>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div>
      <label className="block text-xs font-medium text-gray-600 mb-1.5">{label}</label>
      {children}
    </div>
  );
}
